/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  includes,
  merge,
  isNil,
  get,
  head,
  find,
  isEmpty,
  isEqual,
  map,
  size,
} from 'lodash';
import {
  DataSource,
  SitesService,
  CompanyUtilService,
  TransferGroupService,
  TransferGroupTemplateService,
  EventsService,
} from '@shared/services';
import {
  ENTITY_TYPES,
  IBaseFormService,
  ISite,
  TRANSFER_TYPE,
  ILimit,
  IFilterQuery,
  IApiListResponse,
  IEvent,
  ITransferGroupFormFieldPath,
  ISiteResponse,
  ObjectId,
  EntityId,
} from '@shared/types';
import { lazyInject } from '@app/containers/base/decorators';
import {
  getFormFieldValue,
  setFormFieldValue,
  getFormControl,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { TransferBaseFormService } from '../transfer-common';
import { BehaviorSubject } from 'rxjs';
import {
  isSiteTypePrimaryWarehouse,
  setPrimaryAndWarehouseTop,
  getEventRelatedAndWarehouseSites,
  addStoreNumberForSites,
} from '../transfer/transfer.util';
import {
  getSubtractDateString,
  toDate,
  toMoment,
} from '@app/shared/utils/date-utils';
import {
  ONLY_DATE_SUBSTRING_LENGTH,
  MAX_PAGE_SIZE,
} from '@config/global-constants';
import { rx } from '@app/shared/rx';
import {
  composeWithDefaultEventQuery,
  getPreEventTransactionDateRange,
  getTransferEventQueryFilter,
} from '../transfer/transfer.query.util';
import {
  getCreateAutoPopulateData,
  getObservableValue,
} from '@app/shared/utils';
import {
  ITemplateChangeOptions,
  ITransferGroupTemplate,
} from '../transfer-group-template/transfer-group-template.types';
import { AutoPopulateService } from '../auto-populate/auto-populate.service';
import { IFetchSiteOptions } from '../auto-populate/auto-populate.types';

@Injectable()
export class TransferGroupAddFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP>
{
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(TransferGroupService)
  public transferGroupService: TransferGroupService;
  @lazyInject(TransferGroupTemplateService)
  public transferGroupTemplateService: TransferGroupTemplateService;
  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(AutoPopulateService)
  public autoPopulateService: AutoPopulateService;

  public formFieldPath: ITransferGroupFormFieldPath = {
    transferType: 'transfer_type',
    eventId: 'event_id',
    siteId: 'site_id',
    transactionDate: 'transaction_date',
    name: 'name',
    virtualTransactionDate: 'virtual.transaction_date',
    templateId: 'template_id',
    siteIdsTo: 'site_ids_to',
    virtualTemplateId: 'virtual.template_id',
    virtualSiteIdsTo: 'virtual.site_ids_to',
  };

  public eventIdDataSource: DataSource;
  public siteIdDataSource: DataSource;
  public templateIdDataSource: DataSource;
  public selectedEvent: IEvent;
  // Used to mark the execution of automatic filling.
  // Once, it executed once, the logic of automatic filling will not be triggered again.
  public triggeredOncePopulate: boolean = false;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };
  public siteList$: BehaviorSubject<ISite[]> = new BehaviorSubject([]);

  public eventList: IEvent[] = [];
  public openedEventList: IEvent[] = [];

  public constructor(public injector: Injector) {
    super(injector);
    this.initEventDataSource();
    this.initSiteIdDataSource();
    this.initTemplateDataSource();
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public initSiteIdDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = this.siteIdDataSourceFetchList.bind(this);
  }

  public initTemplateDataSource(): void {
    this.templateIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE,
    );
    this.templateIdDataSource.fetchList =
      this.templateDataSourceFetchList.bind(this);
  }

  public templateDataSourceFetchList() {
    const userQuery = {
      select: [
        'name',
        'site_id',
        'transfer_group_name',
        'site_name',
        'store_number',
        'transfer_type',
        'site_ids_to',
      ],
      filter: 'is_active eq true',
      orderby: 'name asc',
    };
    const canView = this.canViewTransferGroupTemplate();
    if (canView) {
      return this.templateIdDataSource.service.fetchList(userQuery).toPromise();
    }

    return Promise.resolve({ items: [] });
  }

  public onEventChange(selectedEvent: IEvent): void {
    if (this.isTemplateSelected()) {
      const siteId = this.getFormFieldValue(this.formFieldPath.siteId);
      const availableSites = this.getSortedEventRelatedSites();
      const isCurrentSiteAvailable = includes(
        map(availableSites, 'id'),
        siteId,
      );
      if (!isCurrentSiteAvailable && this.selectedEvent) {
        this.getFormFieldControl(this.formFieldPath.siteId).reset();
      }
    } else {
      this.getFormFieldControl(this.formFieldPath.siteId).reset();
      this.getFormFieldControl(this.formFieldPath.transactionDate).reset();
      this.getFormFieldControl(
        this.formFieldPath.virtualTransactionDate,
      ).reset();
    }

    this.siteIdDataSource?.refresh();
    const salesStartDateTimeUTC = head(
      selectedEvent.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );

    if (this.isPreEventTypeTransfer()) {
      const { minimum, maximum } = getPreEventTransactionDateRange(
        companyCurrentDate,
        salesStartDate,
      );
      this.transactionDateLimit.minimumObs.next(minimum);
      this.transactionDateLimit.maximumObs.next(maximum);

      const autoPopulateData = getCreateAutoPopulateData(this.routeState$);
      if (autoPopulateData?.site_id && !this.triggeredOncePopulate) {
        const transactionDate = get(autoPopulateData, 'transaction_date');

        if (this.isAvailableSiteId(autoPopulateData?.site_id)) {
          this.triggeredOncePopulate = true;
          setFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
            autoPopulateData?.site_id,
            false,
          );
        }
        if (
          toMoment(transactionDate).isSameOrBefore(toMoment(maximum)) &&
          toMoment(transactionDate).isSameOrAfter(toMoment(minimum))
        ) {
          setFormFieldValue(
            this.form,
            this.formFieldPath.transactionDate,
            transactionDate,
            false,
          );
        }
      } else {
        setFormFieldValue(
          this.form,
          this.formFieldPath.transactionDate,
          companyCurrentDate,
        );
      }
    } else if (this.isDuringEventTransfer()) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        salesStartDate,
      );
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransactionDate,
        salesStartDateTimeUTC,
      );

      this.fetchSiteList(
        {},
        {
          autoPopulateIsEnable: true,
        },
      );
    }
  }

  public siteIdDataSourceFetchList(
    query: IFilterQuery,
    fetchSiteOptions?: IFetchSiteOptions,
  ): Promise<IApiListResponse> {
    const defaultOptions = {
      isFilterEventRelatedAndWareHouseSites: true,
      autoPopulateIsEnable: false,
    };
    const mergedOptions = merge(defaultOptions, fetchSiteOptions);

    return this.fetchSiteList(query, mergedOptions).then(
      (result) => result,
      (error) => {
        this.siteIdDataSource.error.next(error);

        return Promise.reject(error);
      },
    );
  }

  public fetchSiteList(
    query: IFilterQuery,
    fetchSiteOptions?: IFetchSiteOptions,
  ): Promise<ISiteResponse> {
    const defaultOptions = {
      // when popup ready, we fetch sites list,it determines if is or not filter got sites when it popup modal init.
      isFilterEventRelatedAndWareHouseSites: true,
      // when popup ready, we fetch sites list, it determines first time that auto populate's logic should or not work.
      autoPopulateIsEnable: true,
    };
    const mergedOptions = merge(defaultOptions, fetchSiteOptions);
    if (!this.form) {
      return Promise.resolve({
        items: [],
      } as ISiteResponse);
    }

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        select: [
          'name',
          'id',
          'type',
          'business_type',
          'store_number',
          'metadata',
        ],
        filter_by_assigned_sites: true,
        include_inactive: false,
      },
      query,
      {
        top: MAX_PAGE_SIZE,
      },
    );

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((response) => {
          const eventSites: ISite[] = get(
            this.selectedEvent,
            'available_sites',
            [],
          );
          let sites: ISite[] = response.items as ISite[];

          if (
            this.isEventType() &&
            mergedOptions.isFilterEventRelatedAndWareHouseSites &&
            this.selectedEvent
          ) {
            sites = getEventRelatedAndWarehouseSites(sites, eventSites);
          }
          sites = setPrimaryAndWarehouseTop(sites);

          if (this.isDuringEventTransfer()) {
            const headElementOfSites: ISite = head(sites);
            const sortedAvailableSites = this.getSortedEventRelatedSites();
            const eventRelatedSiteIds = map(sortedAvailableSites, 'id');
            const selectedTemplateDoc = this.getSelectedTemplateDocById(
              getFormFieldValue(
                this.form,
                this.formFieldPath.virtualTemplateId,
              ),
            );
            const isTemplateSiteAssignToEvent = includes(
              eventRelatedSiteIds,
              get(selectedTemplateDoc, this.formFieldPath.siteId),
            );
            const lastSiteIsAssignedToEvent = includes(
              eventRelatedSiteIds,
              getFormFieldValue(this.form, this.formFieldPath.siteId),
            );
            const autoPopulateData = getCreateAutoPopulateData(
              this.routeState$,
            );
            if (
              (isSiteTypePrimaryWarehouse(headElementOfSites) ||
                !isEmpty(autoPopulateData?.site_id)) &&
              !this.isTemplateSelected()
            ) {
              if (
                !isEmpty(autoPopulateData?.site_id) &&
                mergedOptions.autoPopulateIsEnable &&
                !this.triggeredOncePopulate &&
                this.isAvailableSiteId(autoPopulateData?.site_id)
              ) {
                this.triggeredOncePopulate = true;
                setFormFieldValue(
                  this.form,
                  this.formFieldPath.siteId,
                  autoPopulateData.site_id,
                );
              } else if (isSiteTypePrimaryWarehouse(headElementOfSites)) {
                // For new transfer group, selectedEvent must exist,but auto populate's fetch site before fetch event,
                // So if not add this judgement, will set site twice.
                if (!isEmpty(this.selectedEvent)) {
                  setFormFieldValue(
                    this.form,
                    this.formFieldPath.siteId,
                    get(headElementOfSites, 'id'),
                  );
                }
              }
            }

            if (
              isSiteTypePrimaryWarehouse(headElementOfSites) &&
              this.isTemplateSelected() &&
              !lastSiteIsAssignedToEvent
            ) {
              if (!isTemplateSiteAssignToEvent) {
                setFormFieldValue(
                  this.form,
                  this.formFieldPath.siteId,
                  get(headElementOfSites, 'id'),
                );
              } else {
                setFormFieldValue(
                  this.form,
                  this.formFieldPath.siteId,
                  get(selectedTemplateDoc, this.formFieldPath.siteId),
                );
              }
            }
          }

          response.items = addStoreNumberForSites(sites);

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.siteIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public eventDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    if (!this.isEventType()) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse);
    }

    const transferTypeValue: TRANSFER_TYPE = getFormFieldValue(
      this.form,
      this.formFieldPath.transferType,
    );

    const userQuery = {
      filter: getTransferEventQueryFilter(transferTypeValue),
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map((response) => {
          this.eventList = response.items as IEvent[];

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public isEventType(): boolean {
    const transferTypeValue: TRANSFER_TYPE = getFormFieldValue(
      this.form,
      this.formFieldPath.transferType,
    );
    const eventTypes = [
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      TRANSFER_TYPE.DURING_EVENT_TRANSFER,
    ];

    return includes(eventTypes, transferTypeValue);
  }

  public popWindowFormReady(): Function {
    return async (form: FormGroup): Promise<void> => {
      this.selectedEvent = null;
      this.form = form;
      this.transactionDateLimit.minimumObs.next(
        getSubtractDateString(
          toDate().toISOString(),
          ONLY_DATE_SUBSTRING_LENGTH,
        ),
      );
      this.eventIdDataSource?.refresh();
      this.siteIdDataSource?.refresh();
      this.templateIdDataSource?.refresh();

      this.triggeredOncePopulate = false;

      this.transactionDateLimit.maximumObs.next(null);
      const siteResponse = await this.fetchSiteList(
        {},
        {
          isFilterEventRelatedAndWareHouseSites: false,
          autoPopulateIsEnable: false,
        },
      );
      this.siteList$.next(get(siteResponse, 'items', []) as ISite[]);
      const userQuery = {
        filter: getTransferEventQueryFilter(
          TRANSFER_TYPE.DURING_EVENT_TRANSFER,
        ),
      };
      const openEventList = await this.eventsService
        .fetchList(composeWithDefaultEventQuery(userQuery))
        .pipe(
          rx.first(),
          rx.map((resp) => resp.items),
        )
        .toPromise();

      this.openedEventList = openEventList as IEvent[];

      getFormControl(form, this.formFieldPath.transferType)
        .valueChanges.pipe(rx.distinctUntilChanged())
        .subscribe(async () => {
          this.eventIdDataSource?.refresh();
          this.siteIdDataSource?.refresh();

          if (!this.isTemplateSelected()) {
            this.getFormFieldControl(this.formFieldPath.siteId).reset();
          }
          this.getFormFieldControl(this.formFieldPath.eventId).reset();
          this.getFormFieldControl(this.formFieldPath.transactionDate).reset();
          this.getFormFieldControl(
            this.formFieldPath.virtualTransactionDate,
          ).reset();

          if (!this.isDuringEventTransfer()) {
            const companyCurrentDate = this.companyService.getCurrentTime();

            setFormFieldValue(
              form,
              this.formFieldPath.transactionDate,
              companyCurrentDate,
            );
          } else {
            let salesStartDateTimeUTC = null;
            let salesStartDate = null;
            const response: IApiListResponse =
              await this.eventDataSourceFetchList({} as IFilterQuery);
            const eventItems = response.items as IEvent[];
            if (eventItems?.length === 1) {
              // If there is one Open event, auto Populate it for the Event
              const selectedEvent: IEvent = head(eventItems);
              salesStartDateTimeUTC = head(
                selectedEvent.event_days,
              )?.start_date_time;
              salesStartDate = this.companyService.convertUTCDateWithTimeZone(
                salesStartDateTimeUTC,
                true,
              );

              setFormFieldValue(
                this.form,
                this.formFieldPath.eventId,
                get(selectedEvent, 'id'),
                false,
              );

              setFormFieldValue(
                this.form,
                this.formFieldPath.transactionDate,
                salesStartDate,
                false,
              );
              setFormFieldValue(
                this.form,
                this.formFieldPath.virtualTransactionDate,
                salesStartDateTimeUTC,
                false,
              );
            } else {
              this.getFormFieldControl(
                this.formFieldPath.transactionDate,
              ).reset();
              this.getFormFieldControl(
                this.formFieldPath.virtualTransactionDate,
              ).reset();
            }
          }
        });

      getFormControl(form, this.formFieldPath.eventId)
        .valueChanges.pipe(rx.distinctUntilChanged())
        .subscribe((eventId: ObjectId) => {
          if (!isNil(eventId)) {
            const selectedEvent: IEvent = find(this.eventList, {
              id: eventId,
            });
            if (!isNil(selectedEvent)) {
              this.selectedEvent = selectedEvent;
              this.onEventChange(selectedEvent);
            }
          } else {
            this.selectedEvent = null;
          }
        });

      getFormControl(form, this.formFieldPath.virtualTemplateId)
        .valueChanges.pipe(
          rx.distinctUntilChanged(),
          rx.tap((templateId: EntityId) =>
            this.resetVirtualDestination(templateId),
          ),
          rx.filter((templateId) => !isEmpty(templateId)),
        )
        .subscribe((templateId: EntityId) => {
          const selectedTemplateDoc =
            this.getSelectedTemplateDocById(templateId);
          const type = getFormFieldValue(form, this.formFieldPath.transferType);
          if (
            type !== get(selectedTemplateDoc, this.formFieldPath.transferType)
          ) {
            setFormFieldValue(
              form,
              this.formFieldPath.transferType,
              get(selectedTemplateDoc, this.formFieldPath.transferType),
            );
          }

          setFormFieldValue(
            form,
            this.formFieldPath.name,
            get(selectedTemplateDoc, 'transfer_group_name'),
          );
          const allSitesAssignedToUser = map(
            getObservableValue(this.siteList$),
            'id',
          );
          const currentSiteId = get(
            selectedTemplateDoc,
            this.formFieldPath.siteId,
          );
          const templateSiteIsAssignedToUser = includes(
            allSitesAssignedToUser,
            currentSiteId,
          );
          const formFromSiteId = getFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
          );
          const allSites = getObservableValue(this.siteList$);
          const currentSite = find(allSites, (site) =>
            isEqual(
              site.id,
              get(selectedTemplateDoc, this.formFieldPath.siteId),
            ),
          );
          setFormFieldValue(
            form,
            this.formFieldPath.virtualSiteIdsTo,
            selectedTemplateDoc.site_ids_to,
            false,
          );

          if (!isEmpty(this.selectedEvent) && this.isDuringEventTransfer()) {
            const salesStartDateTimeUTC = head(
              this.selectedEvent.event_days,
            )?.start_date_time;
            const salesStartDate =
              this.companyService.convertUTCDateWithTimeZone(
                salesStartDateTimeUTC,
                true,
              );

            setFormFieldValue(
              this.form,
              this.formFieldPath.transactionDate,
              salesStartDate,
            );
            setFormFieldValue(
              this.form,
              this.formFieldPath.virtualTransactionDate,
              salesStartDateTimeUTC,
            );
          }
          const sortedAvailableSites = this.getSortedEventRelatedSites();
          const eventRelatedSiteIds = map(sortedAvailableSites, 'id');
          const isTemplateSiteAssignToEvent =
            includes(eventRelatedSiteIds, currentSite?.id) &&
            !isEmpty(this.selectedEvent);
          const lastSelectedSiteId = getFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
          );
          const isLastFromAssignedToEvent = includes(
            eventRelatedSiteIds,
            lastSelectedSiteId,
          );
          const firstSiteId = get(head(sortedAvailableSites), 'id');

          if (this.isPreEventTypeTransfer()) {
            this.handleTemplateChangeWithPreTransferType(selectedTemplateDoc, {
              templateSiteIsAssignedToUser,
              isTemplateSiteAssignToEvent,
              isLastFromAssignedToEvent,
            });
          }

          if (this.isDuringEventTransfer()) {
            this.handleTemplateChangeWithDuringTransferType(
              selectedTemplateDoc,
              {
                templateSiteIsAssignedToUser,
                isTemplateSiteAssignToEvent,
                isLastFromAssignedToEvent,
                firstSiteId,
              },
            );
          }
          if (this.isOutsideOfEventTransfer()) {
            if (templateSiteIsAssignedToUser) {
              setFormFieldValue(
                this.form,
                this.formFieldPath.siteId,
                selectedTemplateDoc?.site_id,
              );
            } else if (!formFromSiteId) {
              this.getFormFieldControl(this.formFieldPath.siteId).reset();
            }
          }
        });
      this.autoPopulate();
    };
  }

  public resetVirtualDestination(templateId: EntityId): void {
    if (isEmpty(templateId)) {
      setFormFieldValue(this.form, this.formFieldPath.virtualSiteIdsTo, []);
    }
  }

  public canViewTransferGroupTemplate(): boolean {
    const permissionCheckName = get(
      this.transferGroupTemplateService,
      'config.entityNameToCheckPermissions',
      ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE,
    );

    return this.permissionService.canView(permissionCheckName);
  }

  public getSortedEventRelatedSites(): ISite[] {
    const availableSites = getEventRelatedAndWarehouseSites(
      getObservableValue(this.siteList$),
      get(this.selectedEvent, 'available_sites', []),
    );

    return setPrimaryAndWarehouseTop(availableSites);
  }

  public getSelectedTemplateDocById(templateId: EntityId) {
    return find(
      get(this.templateIdDataSource, ['items', 'value']),
      (doc) => isEqual(doc?.entity_id, templateId) && !isEmpty(templateId),
    );
  }

  public handleTemplateChangeWithPreTransferType(
    selectedTemplateDoc: ITransferGroupTemplate,
    {
      templateSiteIsAssignedToUser,
      isTemplateSiteAssignToEvent,
      isLastFromAssignedToEvent,
    }: ITemplateChangeOptions,
  ): void {
    if (templateSiteIsAssignedToUser && isEmpty(this.selectedEvent)) {
      this.setModalFormTemplateId(selectedTemplateDoc?.site_id);
    } else if (!templateSiteIsAssignedToUser && isEmpty(this.selectedEvent)) {
      this.resetModalFormSiteField();
    } else if (templateSiteIsAssignedToUser && isTemplateSiteAssignToEvent) {
      this.setModalFormTemplateId(selectedTemplateDoc?.site_id);
    } else if (!isLastFromAssignedToEvent) {
      this.resetModalFormSiteField();
    }
  }

  public handleTemplateChangeWithDuringTransferType(
    selectedTemplateDoc: ITransferGroupTemplate,
    {
      templateSiteIsAssignedToUser,
      isTemplateSiteAssignToEvent,
      isLastFromAssignedToEvent,
      firstSiteId,
    }: ITemplateChangeOptions,
  ): void {
    const shouldSetFirstSite =
      !isTemplateSiteAssignToEvent &&
      !isLastFromAssignedToEvent &&
      !isEmpty(this.selectedEvent);
    const eventSizeOne = isEqual(size(this.openedEventList), 1);

    // only exist ont open event list for during transfer and template site assigned to user.
    if (eventSizeOne && templateSiteIsAssignedToUser) {
      const isTemplateSiteAssignToTheOneEvent = includes(
        map(head(this.openedEventList).available_sites, 'id'),
        selectedTemplateDoc?.site_id,
      );
      if (isTemplateSiteAssignToTheOneEvent) {
        this.setModalFormTemplateId(selectedTemplateDoc?.site_id);
      } else if (shouldSetFirstSite) {
        this.setModalFormTemplateId(firstSiteId);
      }

      return;
    }

    // not only one event, and template site assigned to user.
    if (!eventSizeOne && templateSiteIsAssignedToUser) {
      if (isTemplateSiteAssignToEvent || isEmpty(this.selectedEvent)) {
        this.setModalFormTemplateId(selectedTemplateDoc?.site_id);
      } else if (shouldSetFirstSite) {
        this.setModalFormTemplateId(firstSiteId);
      }

      return;
    }

    // template site not assigned to user.
    if (!templateSiteIsAssignedToUser && shouldSetFirstSite) {
      this.setModalFormTemplateId(firstSiteId);
    }

    if (!templateSiteIsAssignedToUser && isEmpty(this.selectedEvent)) {
      this.resetModalFormSiteField();
    }
  }

  public setModalFormTemplateId(templateId: EntityId): void {
    setFormFieldValue(this.form, this.formFieldPath.siteId, templateId);
  }

  public resetModalFormSiteField(): void {
    this.getFormFieldControl(this.formFieldPath.siteId).reset();
  }

  public isTemplateSelected(): boolean {
    return !isEmpty(
      this.getFormFieldValue(this.formFieldPath.virtualTemplateId),
    );
  }

  public async autoPopulate(): Promise<void> {
    if (!this.isNewMode()) {
      return;
    }

    const autoPopulateData = getCreateAutoPopulateData(this.routeState$);

    if (isEmpty(autoPopulateData)) {
      return;
    }

    this.autoPopulateService.populateAsynchronousEvent(
      this.form,
      this.formFieldPath.eventId,
      autoPopulateData?.event_id,
      this.eventIdDataSource,
    );

    if (
      this.isOutsideOfEventTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      )
    ) {
      const currentDate = this.companyService.getCurrentTime();
      this.autoPopulateService.populateAsynchronousSite(
        this.form,
        this.formFieldPath.siteId,
        autoPopulateData?.site_id,
        getObservableValue(this.siteList$),
      );
      // why the out side type for auto populate will set the date,
      // because, we set the other types's date in event change.
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        currentDate,
      );
      // when execute auto populate, the triggeredOncePopulate  should flag as true.
      // To avoid when create new out side transfer group, then switch to another type,
      // trigger event change and then the triggeredOncePopulate is still false.
      this.triggeredOncePopulate = true;
    } else {
      await this.eventIdDataSource.nextPage();
      const isAvailableEventId = !isEmpty(
        find(this.eventList, { id: autoPopulateData?.event_id }),
      );
      if (!isAvailableEventId) {
        this.autoPopulateService.populateAsynchronousSite(
          this.form,
          this.formFieldPath.siteId,
          autoPopulateData?.site_id,
          getObservableValue(this.siteList$),
        );
        this.triggeredOncePopulate = true;
      }
    }
  }

  public isAvailableSiteId(siteId: EntityId): boolean {
    const availableSites = getObservableValue(this.siteList$);

    return !isEmpty(find(availableSites, { id: siteId }));
  }
}
